import { useEffect, useMemo } from 'react';
import { AsYouType, getCountries, getCountryCallingCode, isSupportedCountry, parseDigits, parsePhoneNumberFromString, } from 'libphonenumber-js/max';
import metadata from 'libphonenumber-js/metadata.min.json';
import warning from 'warning';
export function useControlledWarning(controlled, value) {
    if (__DEV__) {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
            const createMessage = (preamble) => `${preamble} <PhoneInput /> should not switch from controlled to uncontrolled (or vice versa). Decide between using a controlled or uncontrolled <PhoneInput /> for the lifetime of the component. Check the \`value\` prop being passed in.`;
            warning(!(controlled && value == null), createMessage('<PhoneInput /> is changing from controlled to uncontrolled.'));
            warning(!(!controlled && value != null), createMessage('<PhoneInput /> is changing from uncontrolled to controlled.'));
        }, [controlled, value]);
    }
}
export function transformValue(value) {
    if (value === undefined) {
        return '';
    }
    const asYouType = new AsYouType(undefined);
    asYouType.input(value);
    const phoneNumber = asYouType.getNumber();
    if (phoneNumber) {
        return parseDigits(phoneNumber.formatNational());
    }
    return '';
}
export function stripCountryCallingCode(value) {
    const countryCallingCode = Object.keys(metadata.country_calling_codes).find((callingCode) => value.indexOf(callingCode) === '+'.length);
    if (countryCallingCode) {
        return value.slice(`+${countryCallingCode}`.length);
    }
    return '';
}
export function isCountryCode(countryCode) {
    return isSupportedCountry(countryCode);
}
export function migrateNationalNumber(country, value) {
    const international = value[0] === '+';
    if (international) {
        if (isCountryCode(country) && value.indexOf(`+${getCountryCallingCode(country)}`) === 0) {
            return stripCountryCallingCode(value);
        }
        return '';
    }
    return value;
}
export function toE164(country, value) {
    if (!value) {
        return undefined;
    }
    const international = value[0] === '+';
    if (international) {
        if (value === '+') {
            return undefined;
        }
        return value;
    }
    if (isCountryCode(country)) {
        const formatter = new AsYouType(country);
        formatter.input(value);
        const phoneNumber = formatter.getNumber();
        if (phoneNumber && phoneNumber.nationalNumber) {
            return `+${getCountryCallingCode(country)}${phoneNumber.nationalNumber}`;
        }
    }
    return undefined;
}
export function getCountryFromPartialInputValue(country, value) {
    if (!value || value === '+') {
        return country;
    }
    const asYouType = new AsYouType();
    asYouType.input(value);
    const derivedCountry = asYouType.getCountry();
    if (derivedCountry) {
        return derivedCountry;
    }
    return country;
}
export function getCountryFromPartialE164(country, value) {
    const phoneNumber = parsePhoneNumberFromString(value || '');
    if (phoneNumber && phoneNumber.country) {
        return phoneNumber.country;
    }
    return country;
}
export function sortAlphabetically(a, b) {
    if (a.label < b.label) {
        return -1;
    }
    if (a.label > b.label) {
        return 1;
    }
    return 0;
}
export function useCountries(translations) {
    return useMemo(() => getCountries()
        .map((code) => ({ code, label: (translations === null || translations === void 0 ? void 0 : translations[code]) || code }))
        .sort(sortAlphabetically), [translations]);
}
